import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const Section = ({ id, title, children }) => (
    <section id={id} className="mb-16 scroll-mt-32">
        <h2 className="font-serif-lux text-3xl md:text-4xl leading-tight mb-6">
            {title}
        </h2>
        <div className="space-y-4 text-[15px] leading-relaxed text-[#1C1E1D]/80">
            {children}
        </div>
    </section>
);

const Label = ({ children }) => (
    <div className="tracking-luxe text-[#B89C6A] mt-6 mb-2">{children}</div>
);

export default function LegalPage() {
    return (
        <div data-testid="legal-page" className="min-h-screen bg-[#FAF9F6]">
            <Navbar />
            <main className="pt-32 pb-24">
                <div className="max-w-4xl mx-auto px-6 md:px-12">
                    {/* Header */}
                    <div className="mb-16">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-[#B89C6A]" />
                            <span className="tracking-luxe text-[#B89C6A]">
                                Mentions légales · RGPD
                            </span>
                        </div>
                        <h1 className="font-serif-lux text-4xl md:text-5xl lg:text-6xl leading-[1.02]">
                            Informations{" "}
                            <em className="italic text-[#B89C6A]">
                                légales
                            </em>{" "}
                            &amp; confidentialité.
                        </h1>
                        <p className="mt-6 text-[14px] text-[#1C1E1D]/60">
                            Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                        </p>
                    </div>

                    {/* Table of contents */}
                    <nav className="mb-16 lux-card p-8">
                        <div className="tracking-luxe text-[#B89C6A] mb-4">
                            Sommaire
                        </div>
                        <ol className="space-y-2 text-[14px] font-serif-lux text-lg">
                            {[
                                { id: "editeur", label: "1. Éditeur du site" },
                                { id: "hebergement", label: "2. Hébergement" },
                                { id: "propriete", label: "3. Propriété intellectuelle" },
                                { id: "assurance", label: "4. Assurance professionnelle" },
                                { id: "rgpd", label: "5. Politique de confidentialité (RGPD)" },
                                { id: "cookies", label: "6. Cookies" },
                                { id: "droits", label: "7. Vos droits" },
                                { id: "contact-legal", label: "8. Nous contacter" },
                            ].map((i) => (
                                <li key={i.id}>
                                    <a
                                        href={`#${i.id}`}
                                        className="hover:text-[#B89C6A] transition-colors"
                                    >
                                        {i.label}
                                    </a>
                                </li>
                            ))}
                        </ol>
                    </nav>

                    <Section id="editeur" title="1. Éditeur du site">
                        <Label>Raison sociale</Label>
                        <p>CM RENOV</p>
                        <Label>Siège social</Label>
                        <p>
                            61 Avenue de Breteuil
                            <br />
                            75007 Paris — France
                        </p>
                        <Label>SIRET</Label>
                        <p>938 168 044 000 15</p>
                        <Label>Directeur de la publication</Label>
                        <p>Le représentant légal de CM RENOV.</p>
                        <Label>Contact</Label>
                        <p>
                            Téléphone :{" "}
                            <a
                                href="tel:+33623762847"
                                className="text-[#B89C6A] hover:underline"
                            >
                                06 23 76 28 47
                            </a>
                            <br />
                            Email :{" "}
                            <a
                                href="mailto:contact@cmrenov.com"
                                className="text-[#B89C6A] hover:underline"
                            >
                                contact@cmrenov.com
                            </a>
                        </p>
                    </Section>

                    <Section id="hebergement" title="2. Hébergement">
                        <p>
                            Le site est hébergé par un prestataire de services d'hébergement web professionnel, sur des serveurs situés au sein de l'Union Européenne.
                        </p>
                        <p>
                            Les demandes concernant l'infrastructure technique peuvent être adressées à{" "}
                            <a
                                href="mailto:contact@cmrenov.com"
                                className="text-[#B89C6A] hover:underline"
                            >
                                contact@cmrenov.com
                            </a>
                            .
                        </p>
                    </Section>

                    <Section id="propriete" title="3. Propriété intellectuelle">
                        <p>
                            L'ensemble des contenus présents sur ce site — textes, photographies, vidéos, illustrations, logo, éléments graphiques et architecture — est la propriété exclusive de CM RENOV, protégé par le Code de la propriété intellectuelle.
                        </p>
                        <p>
                            Toute reproduction, représentation, modification, publication, adaptation, totale ou partielle des éléments du site, quel que soit le moyen ou le procédé utilisé, est interdite sans autorisation écrite préalable de CM RENOV, sous peine de constituer une contrefaçon (articles L. 335-2 et suivants du Code de la propriété intellectuelle).
                        </p>
                    </Section>

                    <Section id="assurance" title="4. Assurance professionnelle">
                        <p>
                            CM RENOV est couverte par une <b>assurance décennale</b> et une{" "}
                            <b>responsabilité civile professionnelle</b> souscrites auprès de :
                        </p>
                        <p className="font-serif-lux text-xl mt-4">
                            France Assurances
                        </p>
                        <p className="text-[13px] text-[#1C1E1D]/60">
                            Couverture géographique : France métropolitaine.
                        </p>
                    </Section>

                    <Section id="rgpd" title="5. Politique de confidentialité (RGPD)">
                        <p>
                            CM RENOV s'engage à ce que la collecte et le traitement des données à caractère personnel effectués via ce site soient conformes au Règlement Général sur la Protection des Données (RGPD, règlement UE 2016/679) et à la loi française « Informatique et Libertés » modifiée.
                        </p>

                        <Label>Responsable du traitement</Label>
                        <p>
                            CM RENOV — 61 Avenue de Breteuil, 75007 Paris — contact@cmrenov.com
                        </p>

                        <Label>Données collectées</Label>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                <b>Formulaire de contact</b> : nom, prénom, email, téléphone, message.
                            </li>
                            <li>
                                <b>Demande de devis</b> : nom, prénom, email, téléphone, données techniques du projet (type, surface, options).
                            </li>
                            <li>
                                <b>Prise de rendez-vous</b> : nom, prénom, email, téléphone, adresse du projet, date et créneau souhaités.
                            </li>
                            <li>
                                <b>Données techniques</b> : cookies strictement nécessaires au fonctionnement du site.
                            </li>
                        </ul>

                        <Label>Finalités du traitement</Label>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Répondre à vos demandes de contact, devis ou prise de rendez-vous.</li>
                            <li>Établir un chiffrage préliminaire de votre projet.</li>
                            <li>Communiquer avec vous dans le cadre de la relation commerciale.</li>
                            <li>Respecter les obligations légales et comptables.</li>
                        </ul>

                        <Label>Base légale</Label>
                        <p>
                            Consentement (article 6.1.a du RGPD) pour les formulaires, et exécution de mesures précontractuelles / du contrat (article 6.1.b) pour les demandes de devis et de rendez-vous.
                        </p>

                        <Label>Destinataires</Label>
                        <p>
                            Les données collectées sont destinées exclusivement aux services habilités de CM RENOV. Elles ne sont ni vendues, ni cédées, ni louées à des tiers.
                        </p>
                        <p>
                            Les emails transactionnels sont envoyés via <b>Resend</b> (prestataire conforme RGPD, serveurs UE) uniquement à des fins de confirmation.
                        </p>

                        <Label>Durée de conservation</Label>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Demandes commerciales sans suite : 3 ans à compter du dernier contact.</li>
                            <li>Clients : 10 ans à compter de la fin de la relation contractuelle (obligations légales et garantie décennale).</li>
                        </ul>
                    </Section>

                    <Section id="cookies" title="6. Cookies">
                        <p>
                            Ce site utilise uniquement des <b>cookies techniques strictement nécessaires</b> à son bon fonctionnement (par exemple, pour mémoriser votre progression dans le simulateur de devis).
                        </p>
                        <p>
                            Aucun cookie publicitaire, de tracking marketing ou de mesure d'audience non anonymisée n'est déposé sans votre consentement explicite.
                        </p>
                        <p>
                            Vous pouvez à tout moment configurer votre navigateur pour refuser les cookies. Le fonctionnement du site pourra en être affecté.
                        </p>
                    </Section>

                    <Section id="droits" title="7. Vos droits">
                        <p>
                            Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants sur vos données personnelles :
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                <b>Droit d'accès</b> — obtenir la confirmation que vos données sont traitées et en recevoir une copie.
                            </li>
                            <li>
                                <b>Droit de rectification</b> — corriger toute donnée inexacte ou incomplète.
                            </li>
                            <li>
                                <b>Droit à l'effacement</b> — demander la suppression de vos données (« droit à l'oubli »).
                            </li>
                            <li>
                                <b>Droit à la limitation</b> du traitement.
                            </li>
                            <li>
                                <b>Droit à la portabilité</b> de vos données.
                            </li>
                            <li>
                                <b>Droit d'opposition</b> au traitement pour motif légitime.
                            </li>
                            <li>
                                <b>Droit de retirer votre consentement</b> à tout moment.
                            </li>
                            <li>
                                <b>Droit de définir des directives</b> relatives à la conservation, à l'effacement et à la communication de vos données après votre décès.
                            </li>
                        </ul>
                        <p className="mt-4">
                            Pour exercer ces droits, adressez votre demande à{" "}
                            <a
                                href="mailto:contact@cmrenov.com"
                                className="text-[#B89C6A] hover:underline"
                            >
                                contact@cmrenov.com
                            </a>
                            . Nous vous répondrons dans un délai maximum d'un mois.
                        </p>
                        <p>
                            Vous disposez également du droit d'introduire une réclamation auprès de la Commission Nationale de l'Informatique et des Libertés (CNIL) :
                        </p>
                        <p className="text-[13px]">
                            CNIL — 3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07
                            <br />
                            <a
                                href="https://www.cnil.fr"
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#B89C6A] hover:underline"
                            >
                                www.cnil.fr
                            </a>
                        </p>
                    </Section>

                    <Section id="contact-legal" title="8. Nous contacter">
                        <p>
                            Pour toute question relative aux présentes mentions légales ou à la politique de confidentialité :
                        </p>
                        <div className="mt-4 p-6 border-l-2 border-[#B89C6A] bg-[#B89C6A]/5">
                            <p className="font-serif-lux text-xl">CM RENOV</p>
                            <p className="mt-2">
                                61 Avenue de Breteuil, 75007 Paris
                                <br />
                                <a
                                    href="tel:+33623762847"
                                    className="text-[#B89C6A] hover:underline"
                                >
                                    06 23 76 28 47
                                </a>{" "}
                                ·{" "}
                                <a
                                    href="mailto:contact@cmrenov.com"
                                    className="text-[#B89C6A] hover:underline"
                                >
                                    contact@cmrenov.com
                                </a>
                            </p>
                        </div>
                    </Section>

                    <div className="mt-16 pt-8 border-t hairline text-center">
                        <Link
                            to="/"
                            data-testid="back-to-home"
                            className="btn-outline inline-flex"
                        >
                            ← Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}
